import React, { useEffect, useState } from 'react';
import { PackagePlus, Plus, Edit, RefreshCw } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, Button, Badge, Modal, Input, Label, Spinner, Switch, Column } from '@/components/ui';
import { ResponsiveTable as DataTable } from '@/components/ui/responsive-table';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import {
  getBillingAddonsAdmin,
  createBillingAddon,
  updateBillingAddon,
  syncBillingAddon,
  getBillingSettings,
  updateBillingSettings,
  type BillingAddon,
  type BillingAddonBody,
} from '@/services/billingService';

type AddonKind = BillingAddon['kind'];
type AddonBilling = BillingAddon['billing'];

/** Editable form state — flattened from BillingAddon for the dialogs. */
interface AddonFormState {
  id: string;
  displayName: string;
  description: string;
  kind: AddonKind;
  billing: AddonBilling;
  tokens: string;
  priceUsd: string;
  currency: string;
  public: boolean;
  archived: boolean;
  order: string;
}

const EMPTY_FORM: AddonFormState = {
  id: '',
  displayName: '',
  description: '',
  kind: 'token_pack',
  billing: 'one_time',
  tokens: '0',
  priceUsd: '0',
  currency: 'USD',
  public: false,
  archived: false,
  order: '0',
};

function formFromAddon(addon: BillingAddon): AddonFormState {
  return {
    id: addon.id,
    displayName: addon.displayName ?? '',
    description: addon.description ?? '',
    kind: addon.kind ?? 'token_pack',
    billing: addon.billing ?? 'one_time',
    tokens: addon.tokens != null ? String(addon.tokens) : '',
    priceUsd: String(addon.priceUsd ?? 0),
    currency: addon.currency ?? 'USD',
    public: !!addon.public,
    archived: !!addon.archived,
    order: String(addon.order ?? 0),
  };
}

function num(v: string, fallback = 0): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

/** Build the payload shared by create and update. */
function bodyFromForm(form: AddonFormState): BillingAddonBody {
  const body: BillingAddonBody = {
    displayName: form.displayName.trim(),
    description: form.description.trim(),
    kind: form.kind,
    billing: form.billing,
    priceUsd: num(form.priceUsd),
    currency: form.currency.trim() || 'USD',
    public: form.public,
    archived: form.archived,
    order: num(form.order),
  };
  if (form.tokens.trim() !== '') body.tokens = num(form.tokens);
  return body;
}

const SYNC_BADGE: Record<string, { label: string; variant: 'success' | 'warning' | 'error' }> = {
  synced: { label: 'Synced', variant: 'success' },
  dirty: { label: 'Dirty', variant: 'warning' },
  error: { label: 'Error', variant: 'error' },
};

const KIND_LABEL: Record<AddonKind, string> = {
  token_pack: 'Token pack',
  esim: 'eSIM',
  seats: 'Seats',
  other: 'Other',
};

function formatPrice(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: (currency || 'USD').toUpperCase(),
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `$${amount.toLocaleString()}`;
  }
}

export function BillingAddonsPage() {
  const { isSuperAdmin } = useAuth();
  const { success, error: showError, info } = useToast();

  const [addons, setAddons] = useState<BillingAddon[]>([]);
  const [loading, setLoading] = useState(true);

  // Overage price card
  const [overagePrice, setOveragePrice] = useState('');
  const [overageLoading, setOverageLoading] = useState(true);
  const [overageSaving, setOverageSaving] = useState(false);

  // Edit dialog
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editForm, setEditForm] = useState<AddonFormState>(EMPTY_FORM);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Create dialog
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<AddonFormState>(EMPTY_FORM);
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Per-row sync spinner
  const [syncingId, setSyncingId] = useState<string | null>(null);

  useEffect(() => {
    if (!isSuperAdmin) return;
    loadAddons();
    loadSettings();
  }, [isSuperAdmin]);

  const loadAddons = async () => {
    try {
      setLoading(true);
      const fetched = await getBillingAddonsAdmin();
      setAddons(fetched);
    } catch (err: any) {
      console.error('Failed to load billing add-ons:', err);
      showError(err?.message ?? 'Failed to load billing add-ons');
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      setOverageLoading(true);
      const s = await getBillingSettings();
      setOveragePrice(s.overagePricePerTokenUsd != null ? String(s.overagePricePerTokenUsd) : '');
    } catch (err: any) {
      console.error('Failed to load billing settings:', err);
      showError(err?.message ?? 'Failed to load billing settings');
    } finally {
      setOverageLoading(false);
    }
  };

  const handleSaveOverage = async () => {
    const value = Number(overagePrice);
    if (!Number.isFinite(value) || value < 0) {
      showError('Overage price must be a non-negative number');
      return;
    }
    setOverageSaving(true);
    try {
      await updateBillingSettings(value);
      success(`Overage price set to ${formatPrice(value, 'USD')} / token`);
      await loadSettings();
    } catch (err: any) {
      showError(err?.message ?? 'Failed to save overage price');
    } finally {
      setOverageSaving(false);
    }
  };

  const openEdit = (addon: BillingAddon) => {
    setEditForm(formFromAddon(addon));
    setEditError(null);
    setIsEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editForm.displayName.trim()) {
      setEditError('Display name is required');
      return;
    }
    setEditSaving(true);
    setEditError(null);
    try {
      await updateBillingAddon(editForm.id, bodyFromForm(editForm));
      success(`Add-on "${editForm.displayName}" updated`);
      setIsEditOpen(false);
      await loadAddons();
    } catch (err: any) {
      console.error('Failed to update add-on:', err);
      setEditError(err?.message ?? 'Failed to update add-on');
    } finally {
      setEditSaving(false);
    }
  };

  const openCreate = () => {
    setCreateForm(EMPTY_FORM);
    setCreateError(null);
    setIsCreateOpen(true);
  };

  const handleCreate = async () => {
    if (!createForm.displayName.trim()) {
      setCreateError('Display name is required');
      return;
    }
    setCreateSaving(true);
    setCreateError(null);
    try {
      const body: BillingAddonBody = {
        ...(createForm.id.trim() ? { id: createForm.id.trim() } : {}),
        ...bodyFromForm(createForm),
      };
      await createBillingAddon(body);
      success(`Add-on "${createForm.displayName}" created`);
      setIsCreateOpen(false);
      await loadAddons();
    } catch (err: any) {
      console.error('Failed to create add-on:', err);
      setCreateError(err?.message ?? 'Failed to create add-on');
    } finally {
      setCreateSaving(false);
    }
  };

  const handleSync = async (addon: BillingAddon) => {
    setSyncingId(addon.id);
    try {
      const res = await syncBillingAddon(addon.id);
      if (res?.skipped) {
        info(res.reason ? `Sync skipped: ${res.reason}` : 'Stripe sync skipped for this add-on.');
      } else {
        success(`Synced "${addon.displayName}" to Stripe`);
      }
      await loadAddons();
    } catch (err: any) {
      showError(err?.message ?? 'Failed to sync add-on to Stripe');
    } finally {
      setSyncingId(null);
    }
  };

  const columns: Column<BillingAddon>[] = [
    {
      key: 'displayName',
      header: 'Add-on',
      sortable: true,
      render: (_v, addon) => (
        <div>
          <div className="font-medium text-foreground">{addon.displayName}</div>
          <div className="text-xs text-muted-foreground font-mono">{addon.id}</div>
        </div>
      ),
    },
    {
      key: 'kind',
      header: 'Kind',
      render: (_v, addon) => (
        <Badge variant="secondary">{KIND_LABEL[addon.kind] ?? addon.kind}</Badge>
      ),
    },
    {
      key: 'billing',
      header: 'Billing',
      render: (_v, addon) => (
        <Badge variant={addon.billing === 'recurring' ? 'default' : 'secondary'}>
          {addon.billing === 'recurring' ? 'Recurring' : 'One-time'}
        </Badge>
      ),
    },
    {
      key: 'tokens',
      header: 'Tokens',
      render: (_v, addon) =>
        typeof addon.tokens === 'number' ? (
          <span className="text-foreground">{addon.tokens.toLocaleString()}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'price',
      header: 'Price',
      sortable: true,
      sortValue: (addon) => addon.priceUsd ?? 0,
      render: (_v, addon) => (
        <span className="text-foreground">
          {formatPrice(addon.priceUsd ?? 0, addon.currency)}
          {addon.billing === 'recurring' && (
            <span className="text-xs text-muted-foreground"> / mo</span>
          )}
        </span>
      ),
    },
    {
      key: 'visibility',
      header: 'Visibility',
      render: (_v, addon) => (
        <div className="flex items-center gap-1.5 flex-wrap">
          {addon.public ? (
            <Badge variant="success">Public</Badge>
          ) : (
            <Badge variant="secondary">Hidden</Badge>
          )}
          {addon.archived && <Badge variant="warning">Archived</Badge>}
        </div>
      ),
    },
    {
      key: 'sync',
      header: 'Stripe',
      render: (_v, addon) => {
        const status = addon.pricing?.syncStatus;
        const cfg = status ? SYNC_BADGE[status] : undefined;
        return cfg ? (
          <Badge variant={cfg.variant}>{cfg.label}</Badge>
        ) : (
          <span className="text-muted-foreground text-sm">—</span>
        );
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (_v, addon) => (
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => openEdit(addon)}>
            <Edit className="w-3 h-3" />
            Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={syncingId === addon.id}
            onClick={() => handleSync(addon)}
          >
            {syncingId === addon.id ? <Spinner size="sm" /> : <RefreshCw className="w-3 h-3" />}
            Sync
          </Button>
        </div>
      ),
    },
  ];

  if (!isSuperAdmin) return null;

  return (
    <AppShell>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-display font-semibold text-foreground mb-2 flex items-center gap-2">
              <PackagePlus className="w-6 h-6" />
              Add-ons
            </h1>
            <p className="text-body text-muted-foreground">
              Manage purchasable add-ons (token packs, seats, eSIM) and the post-paid overage price.
            </p>
          </div>
          <Button onClick={openCreate}>
            <Plus className="w-4 h-4" />
            Create add-on
          </Button>
        </div>

        {/* Overage price */}
        <Card className="p-6">
          <h3 className="text-h3 font-semibold text-foreground mb-1">Overage price</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Per-token price billed at the end of a period for orgs with post-paid overage enabled.
          </p>
          {overageLoading ? (
            <div className="flex items-center py-2">
              <Spinner size="sm" />
            </div>
          ) : (
            <div className="flex items-end gap-2">
              <div>
                <Label htmlFor="overage-price">$ per token</Label>
                <Input
                  id="overage-price"
                  type="number"
                  step="0.001"
                  min="0"
                  value={overagePrice}
                  onChange={(e) => setOveragePrice(e.target.value)}
                  className="mt-1 w-40"
                />
              </div>
              <Button onClick={handleSaveOverage} disabled={overageSaving}>
                {overageSaving ? <Spinner size="sm" /> : null}
                {overageSaving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          )}
        </Card>

        <Card className="p-6">
          <DataTable
            columns={columns}
            data={addons}
            keyExtractor={(row) => row.id}
            searchable
            searchPlaceholder="Search add-ons by name or id..."
            searchKeys={['id']}
            paginated
            defaultPageSize={10}
            emptyMessage="No add-ons yet"
            loading={loading}
          />
        </Card>
      </div>

      {/* Edit add-on */}
      <Modal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title={`Edit add-on: ${editForm.displayName || editForm.id}`}
        description="Update display, pricing, and visibility for this add-on."
        size="lg"
        footer={
          <div className="flex justify-end gap-2 w-full">
            <Button variant="outline" onClick={() => setIsEditOpen(false)} disabled={editSaving}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={editSaving}>
              {editSaving ? <Spinner size="sm" /> : <Edit className="w-4 h-4" />}
              {editSaving ? 'Saving...' : 'Save changes'}
            </Button>
          </div>
        }
      >
        <AddonFormFields form={editForm} setForm={setEditForm} error={editError} />
      </Modal>

      {/* Create add-on */}
      <Modal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        title="Create add-on"
        description="Define a new purchasable add-on."
        size="lg"
        footer={
          <div className="flex justify-end gap-2 w-full">
            <Button variant="outline" onClick={() => setIsCreateOpen(false)} disabled={createSaving}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={createSaving}>
              {createSaving ? <Spinner size="sm" /> : <Plus className="w-4 h-4" />}
              {createSaving ? 'Creating...' : 'Create add-on'}
            </Button>
          </div>
        }
      >
        <AddonFormFields form={createForm} setForm={setCreateForm} error={createError} showId />
      </Modal>
    </AppShell>
  );
}

// ----------------------------------------------------------------------------
// Shared form fields (create + edit)
// ----------------------------------------------------------------------------

interface AddonFormFieldsProps {
  form: AddonFormState;
  setForm: React.Dispatch<React.SetStateAction<AddonFormState>>;
  error: string | null;
  /** Show the add-on-id input (create only). */
  showId?: boolean;
}

function SwitchRow({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3 py-1">
      <div>
        <Label className="text-sm">{label}</Label>
        {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

const selectClass =
  'mt-1 w-full px-3 py-2 rounded-md border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent';

function AddonFormFields({ form, setForm, error, showId }: AddonFormFieldsProps) {
  const set = <K extends keyof AddonFormState>(key: K, value: AddonFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="space-y-6">
      {error && <p className="text-status-error text-sm">{error}</p>}

      {/* General */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-foreground">General</h3>
        {showId && (
          <div>
            <Label htmlFor="addon-id">Add-on id (optional)</Label>
            <Input
              id="addon-id"
              placeholder="token_pack_1000"
              value={form.id}
              onChange={(e) => set('id', e.target.value)}
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">Leave blank to auto-generate.</p>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="addon-name">Display name</Label>
            <Input
              id="addon-name"
              placeholder="1,000 token pack"
              value={form.displayName}
              onChange={(e) => set('displayName', e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="addon-order">Order</Label>
            <Input
              id="addon-order"
              type="number"
              value={form.order}
              onChange={(e) => set('order', e.target.value)}
              className="mt-1"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="addon-desc">Description</Label>
          <Input
            id="addon-desc"
            placeholder="Short marketing description"
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            className="mt-1"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="addon-kind">Kind</Label>
            <select
              id="addon-kind"
              className={selectClass}
              value={form.kind}
              onChange={(e) => set('kind', e.target.value as AddonKind)}
            >
              <option value="token_pack">Token pack</option>
              <option value="esim">eSIM</option>
              <option value="seats">Seats</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <Label htmlFor="addon-billing">Billing</Label>
            <select
              id="addon-billing"
              className={selectClass}
              value={form.billing}
              onChange={(e) => set('billing', e.target.value as AddonBilling)}
            >
              <option value="one_time">One-time</option>
              <option value="recurring">Recurring</option>
            </select>
          </div>
        </div>
      </div>

      {/* Pricing */}
      <div className="space-y-4 pt-4 border-t border-border">
        <h3 className="text-sm font-semibold text-foreground">Pricing</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="addon-tokens">Tokens</Label>
            <Input
              id="addon-tokens"
              type="number"
              placeholder="(none)"
              value={form.tokens}
              onChange={(e) => set('tokens', e.target.value)}
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">Leave blank for non-token add-ons.</p>
          </div>
          <div>
            <Label htmlFor="addon-price">Price (USD)</Label>
            <Input
              id="addon-price"
              type="number"
              step="0.01"
              value={form.priceUsd}
              onChange={(e) => set('priceUsd', e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="addon-currency">Currency</Label>
            <Input
              id="addon-currency"
              placeholder="USD"
              value={form.currency}
              onChange={(e) => set('currency', e.target.value)}
              className="mt-1"
            />
          </div>
        </div>
      </div>

      {/* Visibility */}
      <div className="space-y-4 pt-4 border-t border-border">
        <h3 className="text-sm font-semibold text-foreground">Visibility</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SwitchRow
            label="Public"
            hint="Listed in the customer add-on catalog."
            checked={form.public}
            onChange={(v) => set('public', v)}
          />
          <SwitchRow
            label="Archived"
            hint="Hidden everywhere but kept for history."
            checked={form.archived}
            onChange={(v) => set('archived', v)}
          />
        </div>
      </div>
    </div>
  );
}
